// src/services/attendance.js
import api from "./api";

const attendanceService = {
  // Get today's attendance record
  getTodayAttendance: async () => {
    return await api.get("/employee/attendance/today");
  },

  // Get attendance history with filters
  getAttendanceHistory: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.mode) params.append("mode", filters.mode);
    if (filters.status) params.append("status", filters.status);
    if (filters.page) params.append("page", filters.page);
    if (filters.limit) params.append("limit", filters.limit || 20);

    return await api.get(`/employee/attendance/history?${params.toString()}`);
  },

  // Get monthly attendance calendar data
  getMonthlyAttendance: async (year, month) => {
    return await api.get(`/employee/attendance/monthly/${year}/${month}`);
  },

  // Clock in with photo and location
  clockIn: async (photoBlob, locationData) => {
    const formData = new FormData();

    // Add photo
    formData.append("photo", photoBlob, "clock-in-photo.jpg");

    // Add location data
    formData.append("latitude", locationData.latitude.toString());
    formData.append("longitude", locationData.longitude.toString());
    formData.append("accuracy", locationData.accuracy.toString());
    formData.append("address", locationData.address || "");
    formData.append("mode", locationData.mode || "office");

    if (locationData.fieldLocationId) {
      formData.append(
        "fieldLocationId",
        locationData.fieldLocationId.toString()
      );
    }

    if (locationData.distanceFromOffice) {
      formData.append(
        "distanceFromOffice",
        locationData.distanceFromOffice.toString()
      );
    }

    // Add device info
    formData.append(
      "deviceInfo",
      JSON.stringify({
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    );

    return await api.post("/employee/attendance/clock-in", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Clock out with photo and location
  clockOut: async (photoBlob, locationData) => {
    const formData = new FormData();

    // Add photo
    formData.append("photo", photoBlob, "clock-out-photo.jpg");

    // Add location data
    formData.append("latitude", locationData.latitude.toString());
    formData.append("longitude", locationData.longitude.toString());
    formData.append("accuracy", locationData.accuracy.toString());
    formData.append("address", locationData.address || "");
    formData.append("mode", locationData.mode || "office");

    if (locationData.fieldLocationId) {
      formData.append(
        "fieldLocationId",
        locationData.fieldLocationId.toString()
      );
    }

    if (locationData.distanceFromOffice) {
      formData.append(
        "distanceFromOffice",
        locationData.distanceFromOffice.toString()
      );
    }

    // Add device info
    formData.append(
      "deviceInfo",
      JSON.stringify({
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    );

    return await api.post("/employee/attendance/clock-out", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Clock in with short leave confirmation (end break early)
  clockInWithLeaveConfirmation: async (photoBlob, locationData) => {
    const formData = new FormData();

    // Add photo
    formData.append("photo", photoBlob, "clock-in-photo.jpg");

    // Add location data
    formData.append("latitude", locationData.latitude.toString());
    formData.append("longitude", locationData.longitude.toString());
    formData.append("accuracy", locationData.accuracy.toString());
    formData.append("address", locationData.address || "");
    formData.append("mode", locationData.mode || "office");

    // Add leave confirmation data
    formData.append("leaveId", locationData.leaveId.toString());
    if (locationData.confirmedEndTime) {
      formData.append("confirmedEndTime", locationData.confirmedEndTime);
    }

    if (locationData.fieldLocationId) {
      formData.append(
        "fieldLocationId",
        locationData.fieldLocationId.toString()
      );
    }

    if (locationData.distanceFromOffice) {
      formData.append(
        "distanceFromOffice",
        locationData.distanceFromOffice.toString()
      );
    }

    // Add device info
    formData.append(
      "deviceInfo",
      JSON.stringify({
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    );

    return await api.post("/employee/attendance/clock-in-with-leave-confirmation", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Update location during work session
  updateLocation: async (locationData) => {
    return await api.post("/employee/attendance/update-location", {
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      accuracy: locationData.accuracy,
      timestamp: new Date().toISOString(),
    });
  },

  // Get attendance statistics
  getStatistics: async (period = "month") => {
    return await api.get(`/employee/attendance/statistics?period=${period}`);
  },

  // Export attendance data
  exportAttendance: async (format = "csv", filters = {}) => {
    const params = new URLSearchParams();
    params.append("format", format);

    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.mode) params.append("mode", filters.mode);

    return await api.get(`/attendance/export?${params.toString()}`, {
      responseType: "blob",
    });
  },

  // Get field locations
  getFieldLocations: async () => {
    return await api.get("/employee/field-locations");
  },

  // Create field location
  createFieldLocation: async (locationData) => {
    return await api.post("/employee/field-locations", locationData);
  },

  // Update field location
  updateFieldLocation: async (id, locationData) => {
    return await api.put(`/employee/field-locations/${id}`, locationData);
  },

  // Delete field location
  deleteFieldLocation: async (id) => {
    return await api.delete(`/employee/field-locations/${id}`);
  },

  // Check if user can clock in/out from current location
  verifyLocation: async (latitude, longitude) => {
    return await api.post("/employee/attendance/verify-location", {
      latitude,
      longitude,
    });
  },

  // Get attendance summary for date range
  getSummary: async (startDate, endDate) => {
    return await api.get("/employee/attendance/summary", {
      params: { startDate, endDate },
    });
  },

  // Break management
  startBreak: async (reason = "Regular break") => {
    return await api.post("/employee/attendance/break/start", { reason });
  },

  endBreak: async () => {
    return await api.post("/employee/attendance/break/end");
  },

  // Get current attendance status
  getStatus: async () => {
    return await api.get("/employee/attendance/status");
  },

  // Overtime management
  overtime: {
    // Employee overtime methods
    requestOvertime: async (data) => {
      return await api.post("/employee/overtime/request", data);
    },

    getOvertimeRequests: async (params = {}) => {
      return await api.get("/employee/overtime/requests", { params });
    },

    cancelOvertimeRequest: async (id) => {
      return await api.put(`/employee/overtime/requests/${id}/cancel`);
    },

    getActiveOvertimeSession: async () => {
      return await api.get("/employee/overtime/session/active");
    },

    // OA-005: Activate pre-scheduled overtime with photo + geolocation
    activatePreScheduled: async (sessionId, formData) => {
      return await api.post(`/employee/overtime-clock/${sessionId}/activate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },

    // Admin overtime methods
    admin: {
      getOvertimeRequests: async (params = {}) => {
        return await api.get("/admin/overtime/requests", { params });
      },

      approveOvertimeRequest: async (id, notes = "", adjustedHours = null, adjustedRate = null) => {
        return await api.post(`/admin/overtime/requests/${id}/approve`, { 
          notes, 
          adjustedHours, 
          adjustedRate 
        });
      },

      rejectOvertimeRequest: async (id, reason = "") => {
        return await api.post(`/admin/overtime/requests/${id}/reject`, { reason });
      },

      getOvertimePolicies: async () => {
        return await api.get("/admin/overtime/policies");
      },

      bulkApproveOvertimeRequests: async (requestIds, notes = "") => {
        const promises = requestIds.map(id =>
          api.post(`/admin/overtime/requests/${id}/approve`, { notes })
        );
        return await Promise.all(promises);
      },

      bulkRejectOvertimeRequests: async (requestIds, reason) => {
        const promises = requestIds.map(id =>
          api.post(`/admin/overtime/requests/${id}/reject`, { reason })
        );
        return await Promise.all(promises);
      },

      getOvertimeStats: async (params = {}) => {
        // This would need a stats endpoint in the backend
        return await api.get("/admin/overtime/stats", { params });
      },

      getOvertimeAnalytics: async (params = {}) => {
        return await api.get("/admin/analytics/overtime", { params });
      },

      exportOvertimeReport: async (params = {}) => {
        return await api.get("/admin/reports/overtime", {
          params,
          responseType: "blob",
        });
      },
    },
  },

  // Working hours management
  workingHours: {
    // Employee methods
    getWorkingHoursConfig: async () => {
      return await api.get("/employee/working-hours-config");
    },

    getWorkingHours: async () => {
      return await api.get("/employee/working-hours");
    },

    // Admin methods
    admin: {
      getWorkingHoursConfig: async () => {
        console.log('🔍 Making API call to /admin/settings/working-hours');
        const response = await api.get("/admin/settings/working-hours");
        console.log('🔍 API Response received:', response);
        return response;
      },

      updateWorkingHoursConfig: async (config) => {
        console.log('🔍 Service sending config to API:', JSON.stringify(config, null, 2));
        const response = await api.put("/admin/settings/working-hours", config);
        console.log('🔍 Save API Response received:', response);
        return response;
      },

      getDepartments: async () => {
        return await api.get("/admin/departments");
      },
    },
  },

  // Auto clock-out
  autoClockOut: async (reason = "End of working hours") => {
    return await api.post("/employee/attendance/auto-clock-out", { reason });
  },

  // Leave management
  leave: {
    // Employee leave methods
    getLeaveBalances: async () => {
      return await api.get("/employee/leave-balances");
    },

    getLeaveRequests: async (params = {}) => {
      return await api.get("/employee/leave-requests", { params });
    },

    submitLeaveRequest: async (data) => {
      return await api.post("/employee/leave-request", data);
    },

    submitUrgentLeaveRequest: async (data) => {
      return await api.post("/employee/urgent-leave-request", data);
    },

    updateLeaveRequest: async (id, data) => {
      return await api.put(`/employee/leave-request/${id}`, data);
    },

    cancelLeaveRequest: async (id) => {
      return await api.delete(`/employee/leave-request/${id}`);
    },

    getLeaveHistory: async (params = {}) => {
      return await api.get("/employee/leave-history", { params });
    },

    // Admin leave methods
    admin: {
      getLeaveRequests: async (params = {}) => {
        return await api.get("/admin/leave-requests", { params });
      },

      approveLeaveRequest: async (id, notes = "") => {
        return await api.post(`/admin/leave-request/${id}/approve`, { notes });
      },

      rejectLeaveRequest: async (id, notes = "") => {
        return await api.post(`/admin/leave-request/${id}/reject`, { notes });
      },

      bulkApproveLeaveRequests: async (requestIds, notes = "") => {
        return await api.post("/admin/leave-requests/bulk-approve", { requestIds, notes });
      },

      getLeaveBalances: async (params = {}) => {
        return await api.get("/admin/leave-balances", { params });
      },

      updateLeaveBalance: async (employeeId, data) => {
        return await api.put(`/admin/leave-balances/employee/${employeeId}`, data);
      },

      bulkUpdateLeaveBalances: async (data) => {
        return await api.post("/admin/leave-balances/bulk-update", data);
      },

      getLeaveTypes: async () => {
        return await api.get("/admin/leave-types");
      },

      updateLeavePolicies: async (data) => {
        return await api.put("/admin/leave-policies", data);
      },

      exportLeaveReport: async (params = {}) => {
        return await api.get("/admin/reports/leave", {
          params,
          responseType: "blob",
        });
      },

      exportLeaveBalancesReport: async (params = {}) => {
        return await api.get("/admin/leave-balances/export", {
          params,
          responseType: "blob",
        });
      },
    },
  },

  // Holiday management
  holidays: {
    // Admin holiday methods
    admin: {
      getHolidays: async (params = {}) => {
        return await api.get("/admin/holidays", { params });
      },

      createHoliday: async (data) => {
        return await api.post("/admin/holidays", data);
      },

      updateHoliday: async (id, data) => {
        return await api.put(`/admin/holidays/${id}`, data);
      },

      deleteHoliday: async (id) => {
        return await api.delete(`/admin/holidays/${id}`);
      },

      importHolidays: async (data) => {
        return await api.post("/admin/holidays/import", data);
      },

      exportHolidays: async (params = {}) => {
        return await api.get("/admin/holidays/export", {
          params,
          responseType: "blob",
        });
      },
    },

    // Employee holiday methods
    getHolidays: async (params = {}) => {
      return await api.get("/employee/holidays", { params });
    },
  },

  // Admin methods
  admin: {
    // Get live attendance data
    getLiveAttendance: async () => {
      return await api.get("/admin/attendance/live");
    },

    // Get attendance analytics
    getAnalytics: async (params = {}) => {
      return await api.get("/admin/analytics/attendance", { params });
    },

    // Get productivity analytics
    getProductivityAnalytics: async (params = {}) => {
      return await api.get("/admin/analytics/productivity", { params });
    },

    // Get attendance records
    getAttendanceRecords: async (params = {}) => {
      return await api.get("/admin/attendance", { params });
    },

    // Get specific attendance record
    getAttendanceRecord: async (id) => {
      return await api.get(`/admin/attendance/${id}`);
    },

    // Update attendance record
    updateAttendanceRecord: async (id, data) => {
      return await api.put(`/admin/attendance/${id}`, data);
    },

    // Create manual entry
    createManualEntry: async (data) => {
      return await api.post("/admin/attendance/manual-entry", data);
    },

    // Export attendance data
    exportAttendanceData: async (params = {}) => {
      return await api.get("/admin/attendance/export", {
        params,
        responseType: "blob",
      });
    },

    // Get employee attendance summary
    getEmployeeAttendanceSummary: async (userId) => {
      return await api.get(`/admin/attendance/summary/${userId}`);
    },

    // Get attendance dashboard
    getAttendanceDashboard: async () => {
      return await api.get("/admin/attendance/dashboard");
    },

    // Get attendance anomalies
    getAttendanceAnomalies: async (params = {}) => {
      return await api.get("/admin/attendance/anomalies", { params });
    },

    // Bulk approve corrections
    bulkApproveCorrections: async (recordIds) => {
      return await api.post("/admin/attendance/bulk-approve", { recordIds });
    },

    // Generate attendance reports
    generateAttendanceReport: async (params = {}) => {
      return await api.get("/admin/reports/attendance", { params });
    },

    // Get employees list
    getEmployees: async () => {
      return await api.get("/admin/employees");
    },
  },
};

export default attendanceService;
